// Слияние ServerEvent в нормализованное состояние (SPEC §4.3). Каждое правило ИДЕМПОТЕНТНО:
// повтор не портит состояние; сведение по seq (committed) и clientMsgId (оптимистичное) — машинная
// защита инвариантов 1/2 (SPEC §4.9). Функции мутируют переданный draft (стиль immer-producer).
//
// Курсоры last-seq НЕ считаются здесь — это делает store.ts по росту maxSeq (SPEC §4.4), чтобы
// слияние оставалось чистой функцией над состоянием.
import { ensureChatMessages, insertSeqOrdered, } from './state.js';
import { resortChats } from './sort.js';
function emit(ctx, d) {
    ctx.emit?.(d);
}
function ensureReceipts(state, chatId) {
    let r = state.receipts[chatId];
    if (!r) {
        r = { delivered: {}, read: {} };
        state.receipts[chatId] = r;
    }
    return r;
}
/** Зеркало myState.unreadCount + локальная карта unread; держим синхронными. */
function setUnread(state, chatId, value) {
    const v = Math.max(0, value);
    state.unread[chatId] = v;
    const chat = state.chats.byId[chatId];
    if (chat)
        chat.myState.unreadCount = v;
}
/** Пересчёт unread по чату: committed-сообщения с seq>lastReadSeq и не своими. */
function recomputeUnread(state, chatId, ctx) {
    const chat = state.chats.byId[chatId];
    const cm = state.messages.byChat[chatId];
    if (!chat || !cm)
        return;
    const lastRead = chat.myState.lastReadSeq;
    let count = 0;
    for (const seq of cm.orderedSeq) {
        if (seq <= lastRead)
            continue;
        const msg = cm.bySeq[seq];
        if (!msg)
            continue;
        if (ctx.selfId && msg.senderId === ctx.selfId)
            continue;
        count++;
    }
    setUnread(state, chatId, count);
}
/** Завести «болванку» чата по id, если сообщение пришло в неизвестный чат (SPEC §4.9). */
function ensureStubChat(state, chatId, ctx) {
    if (state.chats.byId[chatId])
        return;
    const nowIso = new Date(ctx.now()).toISOString();
    const stub = {
        id: chatId,
        type: 'group',
        memberIds: [],
        memberCount: 0,
        createdBy: (ctx.selfId ?? ''),
        createdAt: nowIso,
        myState: {
            muted: false,
            pinned: false,
            archived: false,
            lastReadSeq: 0,
            unreadCount: 0,
        },
    };
    state.chats.byId[chatId] = stub;
}
/** Обновить lastMessage-превью чата по committed-сообщению, если оно новее. */
function bumpLastMessage(state, chatId, msg) {
    const chat = state.chats.byId[chatId];
    if (!chat)
        return;
    const cur = chat.lastMessage;
    if (!cur || msg.seq >= cur.seq) {
        chat.lastMessage = {
            seq: msg.seq,
            senderId: msg.senderId,
            kind: msg.kind,
            preview: previewOf(msg),
            createdAt: msg.createdAt,
        };
    }
}
function previewOf(msg) {
    if (msg.deletedAt)
        return '';
    if (msg.text)
        return msg.text;
    if (msg.voice)
        return msg.voice.transcript?.text ?? '🎤';
    if (msg.attachments && msg.attachments.length > 0)
        return msg.attachments[0].filename;
    return '';
}
/** Положить отправителя в справочник users (lazy-наполнение для офлайн-рендера). */
function noteSender(state, msg) {
    if (state.users[msg.senderId])
        return;
    // имени ещё нет — заведём минимальную запись; полная придёт из chat.updated/getUser
    const placeholder = { id: msg.senderId, name: '' };
    state.users[msg.senderId] = placeholder;
}
/**
 * Апсёрт committed-сообщения в чат: вставка по возрастанию seq (бинпоиск, идемпотентно),
 * обновление обратного индекса, lastMessage, maxSeq. Возвращает true, если это новая ячейка seq.
 */
export function upsertCommitted(state, chatId, msg, ctx) {
    ensureStubChat(state, chatId, ctx);
    const cm = ensureChatMessages(state, chatId);
    const isNewSeq = cm.bySeq[msg.seq] === undefined;
    cm.bySeq[msg.seq] = msg;
    insertSeqOrdered(cm.orderedSeq, msg.seq);
    state.messageIndex[msg.id] = { chatId, seq: msg.seq };
    if (msg.seq > cm.maxSeq)
        cm.maxSeq = msg.seq;
    noteSender(state, msg);
    bumpLastMessage(state, chatId, msg);
    // сведение с оптимистичным по clientMsgId (инвариант 2): снять, если был наш исходящий
    reconcileOptimistic(state, chatId, msg, ctx);
    return isNewSeq;
}
/** Снять оптимистичное сообщение, сведённое с серверным по clientMsgId (инвариант 2, SPEC §4.5.send-4). */
export function reconcileOptimistic(state, chatId, msg, ctx) {
    const cm = state.messages.byChat[chatId];
    if (!cm)
        return;
    const cid = msg.clientMsgId;
    if (cid && cm.optimistic[cid]) {
        delete cm.optimistic[cid];
        const i = cm.optimisticOrder.indexOf(cid);
        if (i >= 0)
            cm.optimisticOrder.splice(i, 1);
        emit(ctx, { t: 'optimistic.merged', clientMsgId: cid, seq: msg.seq });
    }
}
// ── индивидуальные правила слияния (по event.t) ──────────────────────────────
export function applyMessageNew(state, ev, ctx) {
    const { chatId, message } = ev;
    const cm = ensureChatMessages(state, chatId);
    const known = cm.bySeq[message.seq] !== undefined;
    upsertCommitted(state, chatId, message, ctx);
    resortChats(state);
    // live-инкремент unread: чужое, новее lastReadSeq, чат не открыт активно
    const chat = state.chats.byId[chatId];
    if (chat &&
        ctx.selfId &&
        message.senderId !== ctx.selfId &&
        message.seq > chat.myState.lastReadSeq &&
        !state.activeChats[chatId]) {
        // считаем по журналу (а не слепой ++), чтобы повтор того же seq не задваивал (инвариант 1/2)
        recomputeUnread(state, chatId, ctx);
    }
    if (known)
        emit(ctx, { t: 'event.deduped', event: 'message.new', chatId, seq: message.seq });
    else
        emit(ctx, { t: 'event.applied', event: 'message.new', chatId });
}
export function applyMessageEdited(state, ev, ctx) {
    const idx = state.messageIndex[ev.messageId];
    if (!idx) {
        emit(ctx, { t: 'event.deduped', event: 'message.edited', messageId: ev.messageId });
        return;
    }
    const cm = state.messages.byChat[idx.chatId];
    const msg = cm?.bySeq[idx.seq];
    if (!msg || !cm)
        return;
    // Иммутабельно (новая ссылка Message), иначе timeline ссылочно не меняется и лента не
    // перерисовывается — чужая правка не видна в реальном времени.
    const next = { ...msg, text: ev.text, editedAt: ev.editedAt };
    cm.bySeq[idx.seq] = next;
    // если это последнее сообщение — обновим превью
    bumpLastMessage(state, idx.chatId, next);
    emit(ctx, { t: 'event.applied', event: 'message.edited', chatId: idx.chatId });
}
export function applyMessageDeleted(state, ev, ctx) {
    const idx = state.messageIndex[ev.messageId];
    if (!idx) {
        emit(ctx, { t: 'event.deduped', event: 'message.deleted', messageId: ev.messageId });
        return;
    }
    const cm = state.messages.byChat[idx.chatId];
    const msg = cm?.bySeq[idx.seq];
    if (!msg || !cm)
        return;
    // тумбстоун: контент снят, сообщение остаётся в orderedSeq (плотность seq сохраняется). Иммутабельно
    // (новая ссылка Message), иначе чужое удаление не перерисовало бы ленту в реальном времени.
    const next = {
        ...msg,
        deletedAt: ev.deletedAt,
        text: undefined,
        attachments: undefined,
        voice: undefined,
        reactions: undefined,
    };
    cm.bySeq[idx.seq] = next;
    bumpLastMessage(state, idx.chatId, next);
    emit(ctx, { t: 'event.applied', event: 'message.deleted', chatId: idx.chatId });
}
export function applyReactionSet(state, ev, ctx) {
    const idx = state.messageIndex[ev.messageId];
    if (!idx) {
        emit(ctx, { t: 'event.deduped', event: 'reaction.set', messageId: ev.messageId });
        return;
    }
    const cm = state.messages.byChat[idx.chatId];
    const msg = cm?.bySeq[idx.seq];
    if (!msg || !cm)
        return;
    // Иммутабельная замена: новая ссылка Message + свежий массив reactions. Мутация reactions «на месте»
    // не меняла ссылку Message, из-за чего timeline оставался ссылочно-равным и подписчики (useSelector)
    // не перерисовывали ленту — пилюля реакции не появлялась в реальном времени.
    const next = { ...msg, reactions: msg.reactions?.map((r) => ({ ...r, userIds: r.userIds.slice() })) };
    setReactionOn(next, ev.emoji, ev.userId, ev.set, ctx.selfId);
    cm.bySeq[idx.seq] = next;
    emit(ctx, { t: 'event.applied', event: 'reaction.set', chatId: idx.chatId });
}
/** Пересборка ReactionSummary[] по одному (emoji,userId,set). Идемпотентно (множество userId). */
export function setReactionOn(msg, emoji, userId, set, selfId) {
    const list = msg.reactions ?? (msg.reactions = []);
    let summary = list.find((r) => r.emoji === emoji);
    if (set) {
        if (!summary) {
            summary = { emoji, count: 0, userIds: [], mine: false };
            list.push(summary);
        }
        if (!summary.userIds.includes(userId))
            summary.userIds.push(userId);
    }
    else if (summary) {
        const i = summary.userIds.indexOf(userId);
        if (i >= 0)
            summary.userIds.splice(i, 1);
    }
    if (summary) {
        summary.count = summary.userIds.length;
        summary.mine = selfId !== undefined && summary.userIds.includes(selfId);
        if (summary.count === 0) {
            const i = list.indexOf(summary);
            if (i >= 0)
                list.splice(i, 1);
        }
    }
}
export function applyReceiptDelivered(state, ev, ctx) {
    const r = ensureReceipts(state, ev.chatId);
    const cur = r.delivered[ev.userId] ?? 0;
    if (ev.seq > cur)
        r.delivered[ev.userId] = ev.seq; // монотонно
    emit(ctx, { t: 'event.applied', event: 'receipt.delivered', chatId: ev.chatId });
}
export function applyReceiptRead(state, ev, ctx) {
    const r = ensureReceipts(state, ev.chatId);
    const cur = r.read[ev.userId] ?? 0;
    if (ev.seq > cur)
        r.read[ev.userId] = ev.seq; // монотонно
    // моя отметка с другого устройства → продвигаем lastReadSeq и пересчитываем unread (инвариант 4)
    if (ctx.selfId && ev.userId === ctx.selfId) {
        const chat = state.chats.byId[ev.chatId];
        if (chat && ev.seq > chat.myState.lastReadSeq) {
            chat.myState.lastReadSeq = ev.seq; // монотонно, не откатываем
            recomputeUnread(state, ev.chatId, ctx);
        }
    }
    emit(ctx, { t: 'event.applied', event: 'receipt.read', chatId: ev.chatId });
}
export function applyPresence(state, ev, ctx) {
    state.presence[ev.userId] = { state: ev.state, lastSeenAt: ev.lastSeenAt };
    emit(ctx, { t: 'event.applied', event: 'presence.update' });
}
export function applyTyping(state, ev, ctx) {
    // своё typing не показываем
    if (ctx.selfId && ev.userId === ctx.selfId)
        return;
    let perChat = state.typing[ev.chatId];
    if (!perChat) {
        perChat = {};
        state.typing[ev.chatId] = perChat;
    }
    perChat[ev.userId] = { kind: ev.kind, expiresAt: ctx.now() + ctx.typingTtlMs };
    emit(ctx, { t: 'event.applied', event: 'typing', chatId: ev.chatId });
}
export function applyTranscriptReady(state, ev, ctx) {
    const idx = state.messageIndex[ev.messageId];
    if (!idx) {
        emit(ctx, { t: 'event.deduped', event: 'transcript.ready', messageId: ev.messageId });
        return;
    }
    const msg = state.messages.byChat[idx.chatId]?.bySeq[idx.seq];
    if (!msg || !msg.voice)
        return;
    msg.voice.transcript = { messageId: ev.messageId, status: 'ready', text: ev.text };
    emit(ctx, { t: 'event.applied', event: 'transcript.ready', chatId: idx.chatId });
}
export function applyTranscriptFailed(state, ev, ctx) {
    const idx = state.messageIndex[ev.messageId];
    if (!idx) {
        emit(ctx, { t: 'event.deduped', event: 'transcript.failed', messageId: ev.messageId });
        return;
    }
    const msg = state.messages.byChat[idx.chatId]?.bySeq[idx.seq];
    if (!msg || !msg.voice)
        return;
    msg.voice.transcript = { messageId: ev.messageId, status: 'failed' };
    emit(ctx, { t: 'event.applied', event: 'transcript.failed', chatId: idx.chatId });
}
export function applyChatUpdated(state, ev, ctx) {
    const incoming = ev.chat;
    const existing = state.chats.byId[incoming.id];
    // сохранить локально-изменённый («грязный») черновик: серверное эхо своего же patch не затирает
    const localDirty = state.draftDirty[incoming.id] === true;
    const localDraft = state.drafts[incoming.id];
    const merged = { ...incoming };
    if (localDirty && localDraft !== undefined) {
        merged.myState = { ...incoming.myState, draft: localDraft };
    }
    else if (incoming.myState.draft !== undefined) {
        state.drafts[incoming.id] = incoming.myState.draft;
    }
    else if (existing) {
        // сервер не прислал draft — не теряем уже известный
        if (state.drafts[incoming.id] !== undefined && !localDirty) {
            merged.myState = { ...incoming.myState, draft: state.drafts[incoming.id] };
        }
    }
    state.chats.byId[incoming.id] = merged;
    // зеркала
    state.unread[incoming.id] = merged.myState.unreadCount;
    if (merged.myState.draft !== undefined && !localDirty) {
        state.drafts[incoming.id] = merged.myState.draft;
    }
    // справочник участников (имена подтянутся через getUser лениво)
    for (const uid of merged.memberIds) {
        if (!state.users[uid])
            state.users[uid] = { id: uid, name: '' };
    }
    resortChats(state);
    emit(ctx, { t: 'event.applied', event: 'chat.updated', chatId: incoming.id });
}
/** Удаление группы (R2): выкинуть чат и все его следы из состояния (сервер уже вычистил своё). */
export function applyChatDeleted(state, ev, ctx) {
    const cm = state.messages.byChat[ev.chatId];
    if (cm !== undefined) {
        for (const seq of cm.orderedSeq) {
            const msg = cm.bySeq[seq];
            if (msg !== undefined)
                delete state.messageIndex[msg.id];
        }
    }
    delete state.chats.byId[ev.chatId];
    delete state.messages.byChat[ev.chatId];
    delete state.unread[ev.chatId];
    delete state.drafts[ev.chatId];
    delete state.draftDirty[ev.chatId];
    delete state.typing[ev.chatId];
    delete state.receipts[ev.chatId];
    state.chats.order = state.chats.order.filter((id) => id !== ev.chatId);
    emit(ctx, { t: 'event.applied', event: 'chat.deleted', chatId: ev.chatId });
}
export function applyUnreadUpdate(state, ev, ctx) {
    // серверный «источник истины» по непрочитанным — побеждает локальный инкремент (инвариант 4)
    setUnread(state, ev.chatId, ev.unreadCount);
    state.serverTotalUnread = ev.total;
    emit(ctx, { t: 'event.applied', event: 'unread.update', chatId: ev.chatId });
}
export function applyAck(state, ev, ctx) {
    // ack без полного Message: если committed уже есть — снять оптимистичное; иначе пометить sent.
    const cm = state.messages.byChat[ev.chatId];
    const committed = cm?.bySeq[ev.seq];
    if (committed) {
        reconcileOptimistic(state, ev.chatId, committed, ctx);
    }
    else {
        const opt = cm?.optimistic[ev.clientMsgId];
        if (opt)
            opt.localState = 'sent';
    }
    emit(ctx, { t: 'event.applied', event: 'ack', chatId: ev.chatId });
}
export function applySyncDelta(state, ev, ctx) {
    let serverWins = 0;
    for (const msg of ev.messages) {
        const cm = ensureChatMessages(state, ev.chatId);
        const known = cm.bySeq[msg.seq] !== undefined;
        upsertCommitted(state, ev.chatId, msg, ctx); // то же слияние, что message.new; идемпотентно по seq
        if (known)
            serverWins++; // перекрытие live-хвоста — апсёрт переписывает ту же ячейку (сервер побеждает)
    }
    const cm = ensureChatMessages(state, ev.chatId);
    if (ev.upToSeq > cm.maxSeq)
        cm.maxSeq = ev.upToSeq;
    // дельта пришла полным окном с afterSeq → ниже неё дыр нет до minSeq дельты
    recomputeUnread(state, ev.chatId, ctx);
    resortChats(state);
    emit(ctx, { t: 'cache.reconciled', chatId: ev.chatId, serverWins });
    emit(ctx, { t: 'event.applied', event: 'sync.delta', chatId: ev.chatId });
}
/** Единая точка маршрутизации ServerEvent → правило (SPEC §4.3). */
export function mergeServerEvent(state, event, ctx) {
    switch (event.t) {
        case 'message.new':
            return applyMessageNew(state, event, ctx);
        case 'message.edited':
            return applyMessageEdited(state, event, ctx);
        case 'message.deleted':
            return applyMessageDeleted(state, event, ctx);
        case 'reaction.set':
            return applyReactionSet(state, event, ctx);
        case 'receipt.delivered':
            return applyReceiptDelivered(state, event, ctx);
        case 'receipt.read':
            return applyReceiptRead(state, event, ctx);
        case 'presence.update':
            return applyPresence(state, event, ctx);
        case 'typing':
            return applyTyping(state, event, ctx);
        case 'transcript.ready':
            return applyTranscriptReady(state, event, ctx);
        case 'transcript.failed':
            return applyTranscriptFailed(state, event, ctx);
        case 'chat.updated':
            return applyChatUpdated(state, event, ctx);
        case 'chat.deleted':
            return applyChatDeleted(state, event, ctx);
        case 'unread.update':
            return applyUnreadUpdate(state, event, ctx);
        case 'ack':
            return applyAck(state, event, ctx);
        case 'sync.delta':
            return applySyncDelta(state, event, ctx);
        case 'ready':
        case 'error':
            // транспортный хендшейк / event-уровня ошибка — доменно не мутируем (SPEC §4.3)
            return;
    }
}
//# sourceMappingURL=merge.js.map